import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowUpRight } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { ThemeToggle } from '@/Components/ThemeToggle';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { siteConfig } from '@/config/site';

const howItWorksLinks = [
    { label: 'Funding Programs', href: '/funding/overview', description: 'Explore our evaluation plans' },
    { label: 'Trading Rules', href: '/funding/rules', description: 'Understand our guidelines' },
    { label: 'Platform Overview', href: '/platform/overview', description: 'See our trading infrastructure' },
];

const toolsLinks = [
    { label: 'Economic Calendar', href: '/tools/economic-calendar', description: 'Track market events' },
    { label: 'Market News', href: '/tools/news', description: 'Stay informed on markets' },
    { label: 'Technical Analysis', href: '/tools/technical-analysis', description: 'Charts and indicators' },
    { label: 'Trading Calculators', href: '/tools/calculators', description: 'Position sizing tools' },
];

function DropdownMenu({ label, links }: { label: string; links: { label: string; href: string; description: string }[] }) {
    const [open, setOpen] = useState(false);

    return (
        <div
            className="relative"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
            >
                {label}
                <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-200', open && 'rotate-180')} />
            </button>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.96 }}
                        transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                        className="absolute left-0 top-full pt-3 z-50"
                    >
                        <div className="w-64 rounded-xl border border-border bg-card/95 backdrop-blur-xl shadow-2xl shadow-black/10 p-1.5">
                            {links.map((link) => (
                                <Link
                                    key={link.href}
                                    to={link.href}
                                    className="block rounded-lg px-3 py-2.5 group transition-colors duration-150 hover:bg-accent"
                                >
                                    <span className="block text-sm text-foreground group-hover:text-foreground">{link.label}</span>
                                    <span className="block text-xs text-muted-foreground/70 mt-0.5">{link.description}</span>
                                </Link>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export function Navbar() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileHowItWorks, setMobileHowItWorks] = useState(false);
    const [mobileTools, setMobileTools] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handler = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handler, { passive: true });
        return () => window.removeEventListener('scroll', handler);
    }, []);

    return (
        <nav className={cn(
            'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
            scrolled
                ? 'bg-background/80 backdrop-blur-xl border-b border-border/50'
                : 'bg-transparent'
        )}>
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 items-center justify-between">
                    {/* Logo */}
                    <Link to="/" className="flex items-center" aria-label={siteConfig.brand.name}>
                        <img
                            src={siteConfig.brand.logo.wordmark}
                            alt={siteConfig.brand.name}
                            className="h-9 w-auto"
                            width={1000}
                            height={300}
                        />
                    </Link>

                    {/* Desktop Nav */}
                    <div className="hidden lg:flex items-center gap-8">
                        <DropdownMenu label="How It Works" links={howItWorksLinks} />
                        <Link to="/faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
                            FAQs
                        </Link>
                        <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
                            About
                        </Link>
                        <DropdownMenu label="Tools" links={toolsLinks} />
                        <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
                            Contact
                        </Link>
                    </div>

                    {/* Desktop CTA */}
                    <div className="hidden lg:flex items-center gap-3">
                        <ThemeToggle />
                        <a href={siteConfig.portal.login} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm">Login</Button>
                        </a>
                        <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                            <Button size="sm">
                                Get Started
                                <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" />
                            </Button>
                        </a>
                    </div>

                    {/* Mobile actions */}
                    <div className="lg:hidden flex items-center gap-2">
                        <ThemeToggle />
                        <button
                            className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                            onClick={() => setMobileOpen(!mobileOpen)}
                            aria-label="Toggle menu"
                        >
                            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:hidden overflow-hidden border-t border-border/50 bg-background/95 backdrop-blur-xl"
                    >
                        <div className="px-4 py-4 space-y-1">
                            <Link to="/" className="block rounded-lg px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" onClick={() => setMobileOpen(false)}>
                                Home
                            </Link>

                            <button
                                className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                                onClick={() => setMobileHowItWorks(!mobileHowItWorks)}
                            >
                                How It Works
                                <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', mobileHowItWorks && 'rotate-180')} />
                            </button>
                            <AnimatePresence>
                                {mobileHowItWorks && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="ml-4 space-y-1 pb-1">
                                            {howItWorksLinks.map((link) => (
                                                <Link key={link.href} to={link.href} className="block rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" onClick={() => setMobileOpen(false)}>
                                                    {link.label}
                                                </Link>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <Link to="/faq" className="block rounded-lg px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" onClick={() => setMobileOpen(false)}>
                                FAQs
                            </Link>
                            <Link to="/about" className="block rounded-lg px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" onClick={() => setMobileOpen(false)}>
                                About
                            </Link>

                            <button
                                className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                                onClick={() => setMobileTools(!mobileTools)}
                            >
                                Tools
                                <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', mobileTools && 'rotate-180')} />
                            </button>
                            <AnimatePresence>
                                {mobileTools && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="ml-4 space-y-1 pb-1">
                                            {toolsLinks.map((link) => (
                                                <Link key={link.href} to={link.href} className="block rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" onClick={() => setMobileOpen(false)}>
                                                    {link.label}
                                                </Link>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <Link to="/contact" className="block rounded-lg px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" onClick={() => setMobileOpen(false)}>
                                Contact
                            </Link>

                            <div className="border-t border-border/50 pt-4 mt-4 space-y-2">
                                <a href={siteConfig.portal.login} target="_blank" rel="noopener noreferrer" className="block">
                                    <Button variant="outline" className="w-full">Login</Button>
                                </a>
                                <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer" className="block">
                                    <Button className="w-full">
                                        Get Started
                                        <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" />
                                    </Button>
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
}
